import React, { useState, useEffect } from 'react';
import { PriceChartCard } from './PriceChartCard';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { User, Transaction } from '../App';
import { toast } from 'sonner';
import { CreditCard, Copy, Gift, Users, TrendingUp, ArrowUpRight, ArrowDownLeft, Wallet, Settings, X, Trophy, Send, Megaphone, Palette, Twitter, ChevronRight, Smartphone } from 'lucide-react';
import { ShareModal } from './ShareModal';
import { NewsSlider } from './NewsSlider';
import { TransactionDetailsModal } from './TransactionDetailsModal';
import { WelcomePopup } from './WelcomePopup';
import { BoomEventPopup } from './BoomEventPopup';
import { EventEndedPopup } from './EventEndedPopup';
import { WinnerCheckPopup } from './WinnerCheckPopup';
import { EventEndedNoticePopup } from './EventEndedNoticePopup';
import { getBrandName } from '../utils/brand';
import { i18n, tb } from '../utils/i18n';

interface DashboardProps {
  user: User;
  transactions: Transaction[];
  onUpdateBalance: (newBalance: number) => void;
  onAddTransaction: (transaction: Transaction) => void;
  onNavigateToNews?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToEvents?: () => void;
  onInstallApp?: () => void;
  onNavigateToRewards?: () => void;
}

export function Dashboard({ user, transactions, onUpdateBalance, onAddTransaction, onNavigateToNews, onNavigateToSettings, onNavigateToEvents, onInstallApp, onNavigateToRewards }: DashboardProps) {
  // user가 없으면 로딩 화면 표시
  if (!user) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{i18n.t('loadingUserInfo')}</p>
        </div>
      </div>
    );
  }

  // 웰컴 스테이킹 현황 — 홈 카드 하단

  const [welcome, setWelcome] = useState<{principal:number;earned:number;rate:number;daily:number}|null>(null);

  useEffect(() => {

    (async () => {

      try {

        const t = localStorage.getItem('handy-jwt-token');

        if (!t) return;

        const j = await fetch('/api/staking/welcome/me', { headers: { Authorization: `Bearer ${t}` } }).then(r => r.json());

        if (j?.success && j.enabled) setWelcome(j);

      } catch { /* 홈 화면이 이것 때문에 죽으면 안 된다 */ }

    })();

  }, []);


  const [showShareModal, setShowShareModal] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [showWelcomeBonus, setShowWelcomeBonus] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showReferralBanner, setShowReferralBanner] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showBoomEventPopup, setShowBoomEventPopup] = useState(false);
  const [showEventEndedPopup, setShowEventEndedPopup] = useState(false);
  const [showEventEndedNoticePopup, setShowEventEndedNoticePopup] = useState(false);
  const [showWinnerCheckPopup, setShowWinnerCheckPopup] = useState(false);
  const [handyPrice, setHandyPrice] = useState<number | null>(null);
  // 대기 중인 회원가입 에어드롭도 보유 자산에 합산해 표시(사용은 추천코드 인증 후 가능)
  const pendingBonus = Number(user?.pendingSignupBonus || 0);
  // 락업된 물량은 금고가 들고 있어 지갑 잔액에 안 잡힌다.
  // 로그인 응답만 믿으면 재로그인 전까지 안 보이므로 화면에서 직접 불러 합산한다.
  // 재매입 참여 현황 — 홈 카드 하단 (2026-10-02)
  const [royalSum, setRoyalSum] = useState<{ principal: number; daily: number; total: number } | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const t = localStorage.getItem('handy-jwt-token');
        if (!t) return;
        const j = await fetch('/api/royal/info', { headers: { Authorization: `Bearer ${t}` } }).then(r => r.json());
        const act = (j?.myDeposits || []).filter((d: any) => d.status === 'active');
        if (act.length) setRoyalSum({
          principal: act.reduce((x: number, d: any) => x + Number(d.amount || 0), 0),
          daily: act.reduce((x: number, d: any) => x + Number(d.daily || 0), 0),
          total: act.reduce((x: number, d: any) => x + Number(d.total || 0), 0),
        });
      } catch { /* 홈은 떠야 한다 */ }
    })();
  }, [user?.id]);

  const [lockedHandy, setLockedHandy] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const t = localStorage.getItem('handy-jwt-token') || '';
        if (!t) return;
        const r = await fetch('/api/wallet-lockups', { headers: { Authorization: `Bearer ${t}` } });
        const d = await r.json();
        if (d?.success) setLockedHandy(Number(d.totalLocked || 0));
      } catch { /* 조회 실패는 화면을 막지 않는다 */ }
    })();
  }, [user?.walletAddress]);
  const totalHoldings = Number(user?.balance || 0) + pendingBonus + lockedHandy;
  const [priceChange, setPriceChange] = useState<number | null>(null);
  const [winnerInfo, setWinnerInfo] = useState<{
    rank: number | null;
    prizeAmount: number | null;
    hasParticipated: boolean;
  }>({
    rank: null,
    prizeAmount: null,
    hasParticipated: false
  });

  // 시간 기반 인사말 함수
  const getGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 6 && hour < 12) {
      return 'Good morning';
    } else if (hour >= 12 && hour < 18) {
      const afternoonGreetings = [
        'Good afternoon',
        'Have a great day!',
        'Enjoy your day!',
        'Have a wonderful day!',
        'Make it a great day!',
        'Have an amazing day!'
      ];
      return afternoonGreetings[Math.floor(Math.random() * afternoonGreetings.length)];
    } else {
      const eveningGreetings = [
        'Good evening',
        'Have a great evening!',
        'Enjoy your evening!',
        'Have a wonderful evening!',
        'Make it a great evening!',
        'Have an amazing evening!'
      ];
      return eveningGreetings[Math.floor(Math.random() * eveningGreetings.length)];
    }
  };



  useEffect(() => {
    if (!user?.id) return;
    
    // 붐이벤트 관련 자동 팝업(2차 종료 안내 등)은 진행 중인 이벤트가 없고
    // 로그인/가입 때마다 반복 노출되는 문제가 있어 제거함 (2026-09-11).
    // 가입 직후 안내는 App.tsx의 SignupCompletePopup(웰컴보너스 지급 완료)이 담당한다.
    setShowReferralBanner(true);
  }, [user?.id]);

  // CoinGecko 가격 조회
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        // MEXC 실거래가(백엔드 프록시) 우선, 실패 시 코인게코 폴백
        const res = await fetch('/api/price/handy/market');
        const j = res.ok ? await res.json() : null;
        if (j?.price) {
          setHandyPrice(j.price);
          setPriceChange(j.changePct);
        } else {
          const cg = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=handy&vs_currencies=usd&include_24hr_change=true');
          if (cg.ok) {
            const data = await cg.json();
            if (data.handy) { setHandyPrice(data.handy.usd); setPriceChange(data.handy.usd_24h_change); }
          }
        }
      } catch {}
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 60000);
    return () => clearInterval(interval);
  }, []);


  const handleTransactionClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleWelcomeAirdrop = async () => {
    try {
      const token = localStorage.getItem('handy-jwt-token');
      if (!token) {
        toast.error(i18n.t('authRequired'));
        return;
      }

      const response = await fetch('/api/vatton/welcome-bonus/claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '에어드롭 처리 실패');
      }

      const result = await response.json();
      
      // 잔고 업데이트
      await onUpdateBalance(user.balance + result.amount);
      
      // 트랜잭션 추가
      const newTransaction = {
        type: 'Welcome Airdrop',
        amount: result.amount,
        createdAt: new Date().toISOString()
      };
      await onAddTransaction(newTransaction);
      
      toast.success(`Welcome airdrop ${result.amount} wHANDY received!`);
    } catch (error) {
      console.error('에어드롭 처리 오류:', error);
      toast.error('Error processing airdrop.');
    }
  };

  const handleReferralCode = () => {
    setShowReferralModal(true);
  };

  // 웰컴 팝업 관련 함수들
  const handleWelcomeBonusClick = () => {
    setShowWelcomePopup(false);
    setShowWelcomeBonus(true);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem('handy-welcome-popup-seen', 'true');
    setShowWelcomePopup(false);
  };

  const handleWelcomePopupClose = () => {
    setShowWelcomePopup(false);
  };

  const handleBoomEventPopupClose = () => {
    setShowBoomEventPopup(false);
  };

  const handleEventEndedPopupClose = () => {
    localStorage.setItem('handy-event-ended-seen', 'true');
    setShowEventEndedPopup(false);
  };

  const handleEventEndedNoticePopupClose = () => {
    setShowEventEndedNoticePopup(false);
  };

  const handleEventEndedNoticePopupDontShow = () => {
    localStorage.setItem('handy-2nd-event-ended-notice-seen', 'true');
    setShowEventEndedNoticePopup(false);
  };

  const handleBoomEventPopupParticipate = () => {
    setShowBoomEventPopup(false);
    // Events 페이지로 이동하고 events 탭 활성화
    if (onNavigateToEvents) {
      // hash를 먼저 제거하여 events 탭이 활성화되도록 함
      if (window.location.hash === '#news') {
        window.location.hash = '';
      }
      onNavigateToEvents();
    }
  };

  const handleBoomEventPopupDontShow = () => {
    localStorage.setItem('handy-boom-popup-dismissed', 'true');
    setShowBoomEventPopup(false);
  };

  const handleCheckWinner = async () => {
    try {
      const token = localStorage.getItem('handy-jwt-token');
      if (!token) {
        toast.error(i18n.t('loginRequired'));
        return;
      }

      const API_URL = (import.meta as any).env?.VITE_API_URL || '';
      const response = await fetch(`${API_URL}/api/boom/check-winner/1`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('당첨 조회 응답 (수동):', data);
        
        if (data.success) {
          const hasParticipated =
            typeof data.hasParticipated === 'boolean'
              ? data.hasParticipated
              : Boolean(data.rank ?? data.prizeAmount);

          setWinnerInfo({
            rank: data.rank ?? null,
            prizeAmount: data.prizeAmount ?? null,
            hasParticipated: hasParticipated
          });
          
          // 배너 클릭 시에는 참여 여부와 관계없이 팝업 표시 (모든 사용자가 확인 가능)
          setShowWinnerCheckPopup(true);
        }
      } else {
        toast.error(i18n.t('winnerCheckFailed'));
      }
    } catch (error) {
      console.error('당첨 조회 오류:', error);
      toast.error(i18n.t('winnerCheckError'));
    }
  };

  const handleReferralSubmit = async () => {
    if (!referralCode.trim()) {
      toast.error('추천코드를 입력해주세요.');
      return;
    }

    if (isVerifying) {
      return; // 이미 인증 중이면 무시
    }

    setIsVerifying(true);

    try {
      const token = localStorage.getItem('handy-jwt-token');
      if (!token || !user?.id) {
        toast.error(i18n.t('authRequired'));
        setIsVerifying(false);
        return;
      }

      // 1. 추천코드 검증
      const API_URL = (import.meta as any).env?.VITE_API_URL || '';
      const validateResponse = await fetch(`${API_URL}/api/referrals/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ referralCode: referralCode.trim().toUpperCase() })
      });

      console.log('Validate response status:', validateResponse.status);
      console.log('Validate response headers:', validateResponse.headers);

      if (!validateResponse.ok) {
        const responseText = await validateResponse.text();
        console.log('Error response:', responseText);
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.error || '추천코드 검증 실패');
        } catch (parseError) {
          throw new Error(`서버 오류: ${validateResponse.status} - ${responseText}`);
        }
      }

      const responseText = await validateResponse.text();
      console.log('Success response:', responseText);
      const validateResult = JSON.parse(responseText);
      
      if (!validateResult.valid) {
        toast.error('유효하지 않은 추천코드입니다.');
        setIsVerifying(false);
        return;
      }

      // 2. 웰컴 보너스 실제 지급 (블록체인 전송)
      const applyResponse = await fetch(`${API_URL}/api/referrals/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          userId: user.id, 
          referralCode: referralCode.trim().toUpperCase() 
        })
      });

      if (!applyResponse.ok) {
        const responseText = await applyResponse.text();
        console.log('Apply error response:', responseText);
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.error || '웰컴 보너스 지급 실패');
        } catch (parseError) {
          throw new Error(`서버 오류: ${applyResponse.status} - ${responseText}`);
        }
      }

      const applyResponseText = await applyResponse.text();
      console.log('Apply success response:', applyResponseText);
      const applyResult = JSON.parse(applyResponseText);
      
      // 3. 모달 전환 및 UI 업데이트
      setShowReferralModal(false);
      setShowWelcomeBonus(true);
      setReferralCode('');
      
      // 4. 사용자 잔액 새로고침 (블록체인에서 조회)
      setTimeout(async () => {
        try {
          const balanceRes = await fetch(`${API_URL}/api/blockchain/whandy/balance/${user.walletAddress}`);
          if (balanceRes.ok) {
            const balanceData = await balanceRes.json();
            user.balance = parseFloat(balanceData.balance);
          }
          window.location.reload(); // 전체 페이지 새로고침으로 모든 컴포넌트 업데이트
        } catch (err) {
          console.warn('잔액 새로고침 실패:', err);
        }
      }, 2000);
      
      toast.success(`Welcome Bonus 3000 wHANDY received! TX: ${applyResult.txHash?.slice(0, 10)}...`);
      
    } catch (error: any) {
      console.error('추천코드 처리 오류:', error);
      
      // 이미 받은 경우 특별 처리
      if (error.message?.includes('이미 추천코드를 적용했습니다') || 
          error.message?.includes('이미 웰컴 보너스를 수령했습니다')) {
        toast.info(i18n.t('alreadyReceivedBonus'));
      } else {
        toast.error(error.message || 'Error processing referral code.');
      }
    } finally {
      setIsVerifying(false);
    }
  };



  const handleCloseBonusModal = () => {
    setShowWelcomeBonus(false);
    if (dontShowAgain) {
      localStorage.setItem('handy-referral-banner-hidden', 'true');
      setShowReferralBanner(false);
    }
  };

  const hideBanner = () => {
    localStorage.setItem('handy-referral-banner-hidden', 'true');
    setShowReferralBanner(false);
  };

  const copyWalletAddress = () => {
    if (!user?.walletAddress) {
      toast.error(i18n.t('walletAddressNotFound'));
      return;
    }

    // HTTP 환경에서도 작동하는 방식 (execCommand 우선)
    const textArea = document.createElement('textarea');
    textArea.value = user.walletAddress;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success(i18n.t('addressCopied'));
      } else {
        toast.error(i18n.t('addressCopyFailed'));
      }
    } catch (err) {
      console.error('복사 오류:', err);
      toast.error(i18n.t('addressCopyFailed'));
    }

    document.body.removeChild(textArea);
  };

  const copyReferralCode = async () => {
    try {
      await navigator.clipboard.writeText(user.referralCode);
      toast.success('추천코드가 복사되었습니다.');
    } catch (error) {
      toast.error('복사에 실패했습니다.');
    }
  };

  return (
    <div className="h-full bg-background flex flex-col">
      <div className="flex-1 overflow-auto scrollbar-hide p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <h1 className="text-2xl font-light text-foreground">{getGreeting()}</h1>
            <p className="text-muted-foreground">{user.nickname}</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Settings Icon */}
            <button
              onClick={onNavigateToSettings}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
            >
              <Settings className="h-5 w-5 text-muted-foreground" />
            </button>
            
            {/* Referral Code Display */}
            <div className="text-right">
              <p className="text-muted-foreground text-xs">Referral Code</p>
              <p className="text-foreground font-bold text-sm">{user.referralCode}</p>
            </div>
            
            {/* Profile Avatar - Clickable */}
            <button
              onClick={() => setShowShareModal(true)}
              className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg hover:scale-105 transition-transform"
            >
              {user.nickname.charAt(0).toUpperCase()}
            </button>
          </div>
        </div>

        {/* Modern Card */}
        <Card
          className="border-0 text-white relative overflow-hidden"
          style={{
            background: user.walletAddress === '0xHANDYFOUNDATION0000000000000000A888888'
              ? '#0F7B4F'
              : '#E85D00'
          }}
        >
          <CardContent className="p-6">
            {/* 토스처럼: 숫자 크게, 설명은 쉬운 한글 한 줄씩. 주소·영문 라벨은 뺐다 */}
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-2">
                <p className="text-white/90 text-base font-semibold">내 자산</p>
                {user.walletAddress === '0xHANDYFOUNDATION0000000000000000A888888' && (
                  <span className="bg-white/25 text-white text-xs px-2 py-1 rounded-full font-medium">
                    🏛️ Foundation
                  </span>
                )}
              </div>
              <div className="p-2.5 rounded-xl bg-white/15">
                <Wallet className="h-7 w-7 text-white/90" />
              </div>
            </div>
            <p className="text-4xl font-bold leading-tight mt-1">
              {totalHoldings.toLocaleString('ko-KR', { maximumFractionDigits: 0 })}
              <span className="text-lg font-medium text-white/80"> wHANDY</span>
            </p>
            {handyPrice && (
              <>
                <p className="text-2xl font-bold mt-1 text-white/95">
                  ≈ ${(totalHoldings * handyPrice).toFixed(2)}
                </p>
                {priceChange !== null && priceChange !== undefined && (
                  <p className="text-base font-bold mt-1 text-white">
                    어제보다 ${Math.abs(totalHoldings * handyPrice * priceChange / (100 + priceChange)).toFixed(2)}
                    {priceChange >= 0 ? ' 늘었어요' : ' 줄었어요'}
                    {' '}({priceChange >= 0 ? '▲' : '▼'}{Math.abs(priceChange).toFixed(2)}%)
                  </p>
                )}
              </>
            )}

            <div className="flex items-end justify-between"
              style={{ borderTop: '1px solid rgba(255,255,255,.25)', marginTop: 12, paddingTop: 12 }}>
              <div>
                {welcome && welcome.principal > 0 ? (
                  <>
                    <p className="text-white/90 text-base font-semibold">웰컴 스테이킹</p>
                    <p className="text-white text-2xl font-bold mt-1">
                      {welcome.principal.toLocaleString('ko-KR', { maximumFractionDigits: 0 })}
                      <span className="text-white/80 text-base font-medium"> wHANDY</span>
                    </p>
                    <p className="text-white text-base font-bold mt-1">
                      총 수익 +{welcome.earned.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}
                      {' '}· 하루 +{welcome.daily.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}
                      {' '}({welcome.rate.toFixed(2)}%)
                    </p>
                  </>
                ) : (
                  (
                    <>
                      {/* 재매입은 메뉴를 가려둔 기능이다. 메인 카드에만 남아
                          있어서 들어갈 데도 없는 숫자가 떠 있었다. (2026-10-17) */}
                      <p className="text-white/90 text-base font-semibold">스테이킹</p>
                      {welcome?.subs?.principal > 0 ? (
                        <p className="text-white text-xl font-bold mt-0.5">
                          {Number(welcome.subs.principal).toLocaleString('ko-KR')}
                          <span className="text-white/80 text-sm font-medium"> wHANDY</span>
                          <span className="text-white/90 text-sm font-semibold">
                            {' '}· 수익 +{Number(welcome.subs.earned || 0).toLocaleString('ko-KR', { maximumFractionDigits: 2 })}
                          </span>
                        </p>
                      ) : (
                        <p className="text-white/75 text-base mt-0.5">참여 전</p>
                      )}

                    </>
                  )
                )}
              </div>
              <div className="text-right">
                <p className="text-white/90 text-base font-semibold">현재 시세</p>
                <p className="text-white font-bold text-2xl">
                  {handyPrice ? `$${handyPrice.toFixed(5)}` : '조회 중'}
                </p>
              </div>
            </div>

            {/* Card decorative elements */}
          </CardContent>
        </Card>

        {/* 추천코드 인증 안내 - 아직 인증 안 한 사용자에게만 노출.
            실제 입력/인증은 보상 메뉴에서 진행한다(중복 구현 방지) */}
        {!user.referredBy && (
          <Card
            className="border-0 cursor-pointer"
            style={{ background: '#FEF3C7' }}
            onClick={onNavigateToRewards}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#D97706' }}>
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-base" style={{ color: '#92400E' }}>추천코드 인증</p>
                    <p className="text-sm" style={{ color: '#B45309' }}>보상 메뉴에서 추천코드를 인증하세요</p>
                  </div>
                </div>
                <span style={{ background: '#D97706', color: '#FFFFFF', fontWeight: 700, fontSize: '14px', padding: '9px 18px', borderRadius: '9999px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  인증하기
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Welcome Bonus Banner - 추천인 에어드롭 기능 재정비 예정이라 임시로 숨김 (2026-09-10) */}
        {false && showReferralBanner && (
          <Card 
            className="bg-gradient-to-r from-green-500 to-emerald-500 border-0 text-white cursor-pointer hover:from-green-600 hover:to-emerald-600 transition-all duration-300"
            onClick={handleReferralCode}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Gift className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold">{tb('welcomeBannerTitle')}</p>
                  <p className="text-white/80 text-sm">{tb('welcomeBannerDesc')}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReferralCode();
                    }}
                    className="bg-white text-green-600 hover:bg-white/90 h-9 px-4 text-sm font-bold"
                  >
                    {i18n.t('getBonus')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Handy Ecosystem Banners */}
        <div className="space-y-3">
          {/* 실시간 시세 + 차트 */}
          <PriceChartCard />

          {/* 앱 설치 배너 - 설치 가능할 때만 노출 */}
          {onInstallApp && (
            <Card
              className="border-0 cursor-pointer overflow-hidden"
              style={{background: 'linear-gradient(135deg, #1E293B, #334155)'}}
              onClick={onInstallApp}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between text-white">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{background:'#FF6B00'}}>
                      <Smartphone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-base">핸디 월렛 앱 설치</p>
                      <p className="text-white/80 text-sm">홈 화면에 추가하고 앱처럼 사용하세요</p>
                    </div>
                  </div>
                  <span style={{ background: '#FF6B00', color: '#FFFFFF', fontWeight: 700, fontSize: '14px', padding: '9px 18px', borderRadius: '9999px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    설치하기
                  </span>
                </div>
              </CardContent>
            </Card>
          )}


          {/* 매입 프로그램 배너 — 재매입을 가리면서 같이 뺐다 (2026-10-17) */}

          {/* 핸디로얄 게임 오픈 배너 (2026-10-22)
              아래 MEXC·바이낸스 배너와 같은 형식으로 맞춘다 — 원본 비율 유지,
              모서리 16px. 외부 링크가 아니라 앱 안 이동이라 handy:go-game 을 쏜다. */}
          <div
            onClick={() => window.dispatchEvent(new Event('handy:go-game'))}
            className="block cursor-pointer hover:opacity-90 transition-opacity"
            style={{ borderRadius: '16px', overflow: 'hidden', display: 'block' }}>
            <img
              src="/banner-royale-open.jpg?v=1"
              alt="핸디로얄 게임 오픈 — 지금 참여하고 특별한 보상을 획득하세요"
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '16px' }}
              loading="lazy"
            />
          </div>

          {/* HANDY x MEXC 배너.
              제휴 API 키가 확인돼(2026-09-15) UID 자동 검증이 가능해져 다시 올린다.
              형식은 아래 바이낸스 배너와 동일하게 맞춘다(원본 비율 유지 + 모서리 16px). */}
          <a href="https://www.mexc.com/acquisition/custom-sign-up?shareCode=mexc-handy"
             target="_blank" rel="noopener noreferrer"
             className="block cursor-pointer hover:opacity-90 transition-opacity"
             style={{ borderRadius: '16px', overflow: 'hidden', display: 'block' }}>
            <img
              src="/mexc-banner.jpg?v=2"
              alt="Handy x MEXC 가입하고 UID 입력하면 6,000 WHANDY 증정"
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '16px' }}
              loading="lazy"
            />
          </a>

          {/* HANDY x BINANCE 배너 (2026-09-11 추가)
              이미지 원본 비율을 그대로 쓰되(width 100% / height auto), 모서리 둥글기만
              다른 배너 카드와 동일하게 맞춘다. Card 컴포넌트의 rounded-xl 은
              globals.css 의 --radius-xl = 0.75rem + 4px = 16px 이라 같은 값을 쓴다. */}
          <a href="https://www.binance.com/en/events/handy-binance-welcome-bonus"
             target="_blank" rel="noopener noreferrer"
             className="block cursor-pointer hover:opacity-90 transition-opacity"
             style={{ borderRadius: '16px', overflow: 'hidden', display: 'block' }}>
            <img
              src="/banner-binance.jpg?v=2"
              alt="HANDY x BINANCE 신규 가입 웰컴 보너스"
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '16px' }}
              loading="lazy"
            />
          </a>

          {/* HandyLabs.ai 배너 */}
          <a href="https://handylabs.ai" target="_blank" rel="noopener noreferrer"
             className="block">
            <Card className="border-0 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                  style={{background: 'linear-gradient(135deg, #FF6B00, #FF8C00)'}}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between text-white">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <svg viewBox="0 0 100 100" width="28" height="28">
                        <text x="50" y="75" fontFamily="Arial Black" fontSize="75" fontWeight="900" 
                              textAnchor="middle" fill="white">H</text>
                      </svg>
                    </div>
                    <div>
                      <p className="font-bold text-base">핸디랩스 공식 홈페이지</p>
                      <p className="text-white/90 text-sm">handylabs.ai</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/90" />
                </div>
              </CardContent>
            </Card>
          </a>

          {/* Telegram 커뮤니티 배너 */}
          <a href="https://t.me/HandyProject" target="_blank" rel="noopener noreferrer"
             className="block">
            <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{background:'#229ED9'}}>
                      <Send className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-base">핸디 커뮤니티</p>
                      <p className="text-muted-foreground text-sm">텔레그램 오픈채팅 참여하기</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </a>

          {/* Telegram 공지 배너 */}
          <a href="https://t.me/HandyOfficialAnn" target="_blank" rel="noopener noreferrer"
             className="block">
            <Card className="bg-sky-50 dark:bg-sky-900/20 border-sky-200 cursor-pointer hover:bg-sky-100 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{background:'#0EA5E9'}}>
                      <Megaphone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-base">핸디 공식 공지방</p>
                      <p className="text-muted-foreground text-sm">텔레그램 공지 채널</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </a>

          {/* HandyPick 배너 */}
          <a href="https://handypick.io" target="_blank" rel="noopener noreferrer"
             className="block">
            <Card className="border-orange-200 cursor-pointer hover:bg-orange-50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{background: '#FF6B00'}}>
                      <span className="text-white font-black text-lg">P</span>
                    </div>
                    <div>
                      <p className="font-bold text-base">핸디픽 (HandyPick)</p>
                      <p className="text-muted-foreground text-sm">handypick.io — 핸디 앱</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </a>

          {/* HANDY Gallery 배너 */}
          <a href="https://handylabs.ai/handy-gallery/" target="_blank" rel="noopener noreferrer"
             className="block">
            <Card className="border-purple-200 cursor-pointer hover:bg-purple-50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{background:'#A855F7'}}>
                      <Palette className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-base">핸디 갤러리</p>
                      <p className="text-muted-foreground text-sm">핸디 이미지·콘텐츠 모아보기</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </a>

          {/* X (Twitter) 배너 */}
          <a href="https://x.com/HandyProject" target="_blank" rel="noopener noreferrer"
             className="block">
            <Card className="bg-gray-50 dark:bg-gray-900/20 border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{background:'#000000'}}>
                      <Twitter className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-base">공식 X (트위터)</p>
                      <p className="text-muted-foreground text-sm">최신 소식 팔로우하기</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </a>
        </div>

        {/* Push Notification Cards - 종료된 붐이벤트 배너(2차 종료/1차 당첨자조회)는
            진행 중인 이벤트가 없어 제거함 (2026-09-11) */}

        {/* News Slider - 종료된 붐이벤트가 하드코딩돼 있어 제거 (2026-09-11).
            뉴스는 하단 '이벤트' 탭의 실제 뉴스 목록에서 확인 */}

        {/* Recent Activity - 주석처리 (TX 문제 해결 전까지) */}
        {/* <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-foreground font-medium">Recent Transactions</p>
                <p className="text-muted-foreground text-sm">{transactions.length} total</p>
              </div>
            </div>
            
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wallet className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">No transactions yet</p>
                <p className="text-muted-foreground/60 text-sm mt-1">Start your first transaction</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.slice(0, 5).map((tx, index) => (
                  <button
                    key={tx.hash || tx.id || index}
                    onClick={() => handleTransactionClick(tx)}
                    className="w-full flex items-center justify-between p-3 bg-muted rounded-xl hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        tx.amount > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        {tx.amount > 0 ? (
                          <ArrowDownLeft className="h-5 w-5 text-green-600" />
                        ) : (
                          <ArrowUpRight className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      <div className="text-left">
                        <p className="text-foreground font-medium">{tx.type}</p>
                        <p className="text-muted-foreground text-sm">
                          {tx.timestamp ? new Date(tx.timestamp * 1000).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : tx.createdAt ? new Date(tx.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'Invalid Date'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${
                        tx.amount > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {tx.amount > 0 ? '+' : ''}{parseFloat(tx.amount).toFixed(2)} wHANDY
                      </p>
                      <p className="text-muted-foreground text-xs">
                        ≈ ${tx.amount ? Math.abs(tx.amount * 0.5).toFixed(2) : '0.00'}
                      </p>
                    </div>
                  </button>
                ))}
                
                {transactions.length > 5 && (
                  <button className="w-full py-3 text-primary hover:bg-primary/5 rounded-xl transition-colors">
                    View All Transactions
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card> */}
      </div>

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
        user={user} 
      />

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        isOpen={showTransactionDetails}
        onClose={() => setShowTransactionDetails(false)}
        transaction={selectedTransaction}
        handyPrice={handyPrice}
      />

      {/* Welcome Popup */}
      <WelcomePopup
        isOpen={showWelcomePopup}
        onClose={handleWelcomePopupClose}
        onWelcomeBonusClick={handleWelcomeBonusClick}
        onDontShowAgain={handleDontShowAgain}
      />

      {/* Boom Event Popup */}
      <BoomEventPopup
        isOpen={showBoomEventPopup}
        onClose={handleBoomEventPopupClose}
        onParticipate={handleBoomEventPopupParticipate}
        onDontShowAgain={handleBoomEventPopupDontShow}
        onNavigateToNews={onNavigateToEvents ? () => {
          onNavigateToEvents();
          // Events 페이지에서 News 탭으로 이동
          setTimeout(() => {
            window.location.hash = '#news';
          }, 100);
        } : undefined}
        onWelcomeBonusClick={handleReferralCode}
      />

      {/* Event Ended Popup */}
      <EventEndedPopup
        isOpen={showEventEndedPopup}
        onClose={handleEventEndedPopupClose}
      />

      {/* 2차 붐 이벤트 종료 안내 팝업 */}
      <EventEndedNoticePopup
        isOpen={showEventEndedNoticePopup}
        onClose={handleEventEndedNoticePopupClose}
        onDontShowAgain={handleEventEndedNoticePopupDontShow}
      />

      {/* Winner Check Popup */}
      <WinnerCheckPopup
        isOpen={showWinnerCheckPopup}
        onClose={() => {
          setShowWinnerCheckPopup(false);
        }}
        nickname={user?.nickname || ''}
        rank={winnerInfo.rank}
        prizeAmount={winnerInfo.prizeAmount}
        hasParticipated={winnerInfo.hasParticipated}
        onViewAllWinners={() => {
          if (onNavigateToEvents) {
            onNavigateToEvents();
            // News 탭으로 이동하기 위해 hash 설정
            setTimeout(() => {
              window.location.hash = '#news';
            }, 100);
          }
        }}
        onParticipate={handleBoomEventPopupParticipate}
        onDontShowAgain={() => {
          localStorage.setItem('handy-winner-check-popup-seen', 'true');
        }}
      />

      {/* Referral Code Modal */}
      {showReferralModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Welcome Bonus</h3>
              <p className="text-muted-foreground text-sm">Enter referral code to get 3000 wHANDY</p>
            </div>

            <div className="space-y-4">
              <div>
                <Input
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="Enter referral code"
                  className="h-12 text-lg font-semibold border-2 focus:border-green-500 text-center uppercase tracking-wider"
                  autoFocus
                />
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowReferralModal(false)}
                  variant="outline"
                  className="flex-1 h-12 text-base font-semibold"
                  disabled={isVerifying}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReferralSubmit}
                  disabled={!referralCode.trim() || isVerifying}
                  className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white text-base font-semibold"
                >
                  {isVerifying ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    'Verify'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Welcome Bonus Success Modal */}
      {showWelcomeBonus && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Congratulations!</h3>
              <p className="text-muted-foreground text-sm mb-4">Welcome bonus verified successfully</p>
              
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 mb-4">
                <p className="text-green-600 font-bold text-2xl">+3000 wHANDY</p>
                <p className="text-green-600/80 text-sm">has been credited to your wallet</p>
              </div>
            </div>

            <div className="space-y-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-muted-foreground">Don't show again</span>
              </label>
              
              <Button
                onClick={handleCloseBonusModal}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}